import { gql, useMutation } from '@apollo/client';
import {
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { DocumentNode } from 'graphql';
import * as React from 'react';

import { ImportantDateItem } from '@/components/AccordionMembershipCard/components/ImportantDateItem';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PaymentMethod } from '@/components/PaymentMethod';
import {
  ExternalLinkIcon,
  MoreHorizontalIcon,
  StarIcon,
  Trash2Icon,
} from '@/imports/ui/chakra/feather';

import { PaymentMethodEditor_PaymentMethodFragment } from './graphql-types/PaymentMethodEditor_PaymentMethodFragment';
import {
  PaymentMethodEditor_RemovePaymentMethod,
  PaymentMethodEditor_RemovePaymentMethodVariables,
} from './graphql-types/PaymentMethodEditor_RemovePaymentMethod';
import {
  PaymentMethodEditor_UpdatePaymentMethod,
  PaymentMethodEditor_UpdatePaymentMethodVariables,
} from './graphql-types/PaymentMethodEditor_UpdatePaymentMethod';

const REMOVE_PAYMENT_METHOD = gql`
  mutation PaymentMethodEditor_RemovePaymentMethod(
    $input: RemovePaymentMethodInput!
  ) {
    removePaymentMethod(input: $input) {
      user {
        _id
        paymentMethods {
          _id
          isDefault
        }
        membershipData {
          currentMembershipTerm {
            _id
            renewalSetting
            importantDates {
              ...ImportantDateItem_MembershipTermImportantDateFragment
            }
          }
          nextMembershipTerm {
            _id
            renewalSetting
            importantDates {
              ...ImportantDateItem_MembershipTermImportantDateFragment
            }
          }
        }
      }
      organization {
        _id
        paymentMethods {
          _id
          isDefault
        }
        membershipData {
          currentMembershipTerm {
            _id
            renewalSetting
            importantDates {
              ...ImportantDateItem_MembershipTermImportantDateFragment
            }
          }
          nextMembershipTerm {
            _id
            renewalSetting
            importantDates {
              ...ImportantDateItem_MembershipTermImportantDateFragment
            }
          }
        }
      }
    }
  }
  ${ImportantDateItem.importantDateFragment}
`;

const UPDATE_PAYMENT_METHOD = gql`
  mutation PaymentMethodEditor_UpdatePaymentMethod(
    $input: UpdatePaymentMethodInput!
  ) {
    updatePaymentMethod(input: $input) {
      user {
        _id
        paymentMethods {
          _id
          isDefault
        }
      }
      organization {
        _id
        paymentMethods {
          _id
          isDefault
        }
      }
    }
  }
`;

export const PaymentMethodEditor: React.FC<{
  paymentMethod: PaymentMethodEditor_PaymentMethodFragment;
  isEditing: boolean;
  newView?: boolean;
}> & { paymentMethodFragment: DocumentNode } = ({
  paymentMethod,
  isEditing,
  newView = false,
}) => {
  const confirmDialog = useDisclosure();
  const toast = useToast();

  const [removePaymentMethod, removePaymentMethodResult] = useMutation<
    PaymentMethodEditor_RemovePaymentMethod,
    PaymentMethodEditor_RemovePaymentMethodVariables
  >(REMOVE_PAYMENT_METHOD, {
    onError: () =>
      toast({
        description: 'An unexpected error occurred',
        status: 'error',
      }),
  });

  const [updatePaymentMethod, updatePaymentMethodResult] = useMutation<
    PaymentMethodEditor_UpdatePaymentMethod,
    PaymentMethodEditor_UpdatePaymentMethodVariables
  >(UPDATE_PAYMENT_METHOD, {
    onError: () =>
      toast({
        description: 'An unexpected error occurred',
        status: 'error',
      }),
  });

  return (
    <Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <PaymentMethod paymentMethod={paymentMethod} />

        <Menu>
          <MenuButton
            visibility={isEditing ? undefined : 'hidden'}
            size="sm"
            as={IconButton}
            variant={newView? 'ghost' : 'outline'}
            colorScheme="gray"
            aria-label="edit payment method"
            icon={<MoreHorizontalIcon color={newView? 'primary.500' : 'gray.500'} boxSize={newView? '20px' : '16px'} transform={newView? 'rotate(90deg)' : undefined} />}
            isLoading={updatePaymentMethodResult.loading}
          />
          <MenuList>
            <MenuItem
              isDisabled={paymentMethod.isDefault || !paymentMethod.verified}
              icon={<StarIcon boxSize="20px" color="primary.500" />}
              onClick={async () => {
                await updatePaymentMethod({
                  variables: {
                    input: {
                      paymentMethodId: paymentMethod._id,
                      data: { setToNewDefault: true },
                    },
                  },
                });
              }}
            >
              Set As Default Payment Method
            </MenuItem>
            {paymentMethod.canCurrentUserRemoveThisPaymentMethod && (
              <MenuItem icon={<Trash2Icon boxSize="20px" color="primary.500" />} onClick={confirmDialog.onOpen}>
                Delete Payment Method
              </MenuItem>
            )}
            {paymentMethod.__typename === 'PaymentMethod_UsBankAccount' &&
              !paymentMethod.verified &&
              paymentMethod.verificationUrl && (
                <a
                  href={paymentMethod.verificationUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <MenuItem icon={<ExternalLinkIcon boxSize="20px" color="primary.500" />}>Verify</MenuItem>
                </a>
              )}
          </MenuList>
        </Menu>

        <ConfirmDialog
          body={`We will be deleting the payment method ending in ${
            paymentMethod.__typename === 'PaymentMethod_UsBankAccount'
              ? paymentMethod.bankAccountLastFourDigits
              : paymentMethod.__typename === 'PaymentMethod_Card'
              ? paymentMethod.creditCardLastFourDigits
              : '****'
          } from your saved options. Please note that this action cannot be undone.`}
          confirmButtonText="Delete"
          cancelButtonText="Cancel"
          isOpen={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          onConfirm={() =>
            removePaymentMethod({
              variables: {
                input: { paymentMethodId: paymentMethod._id },
              },
            })
          }
          title="Are you sure you want to delete this payment method?"
          shouldCloseOnConfirmation
        />
      </Stack>
    </Stack>
  );
};

PaymentMethodEditor.paymentMethodFragment = gql`
  fragment PaymentMethodEditor_PaymentMethodFragment on PaymentMethod {
    _id
    ...PaymentMethod_PaymentMethodFragment
    ... on PaymentMethod_UsBankAccount {
      verificationUrl
    }
  }
  ${PaymentMethod.paymentMethodFragment}
`;
